// PaletteEditor.tsx — drag-driven palette tuner.
//
// Two-column layout: a small Preview/Contrast pane on the left, and a large
// interactive color picker on the right. The picker plots all role colors
// over a rendered OKLCH chromaticity gradient (so the user can see what
// hues / chromas are reachable) plus a vertical lightness column.
//
// Interaction model:
//   - Left-click + drag a dot      → locks that role and the gradient
//                                    descent solver runs continuously on
//                                    the rest while you move the dot.
//   - Left-click without dragging  → selects the role; its hex / label /
//                                    lock-toggle appear in the footer.
//   - Right-click a dot            → unlocks it (no-op if already unlocked).
//
// The footer only ever shows one role's info at a time so the picker
// dominates the visual real estate.
import { useEffect, useMemo, useRef, useState } from 'react';
import Card from 'react-bootstrap/Card';
import Button from 'react-bootstrap/Button';
import { BsLockFill, BsUnlockFill } from 'react-icons/bs';
import InlineText from '../ui/InlineText';
import type { Palette, LockMap, Role, SolverContext } from './solver';
import {
    ROLES, ROLE_LABELS,
    gradientStep, SEPARATE_PAIRS, separationFloor,
} from './solver';
import type { OKLCH } from './oklch';
import {
    oklchToHex, hexToOklch, rgbToHex,
    oklabToLinearRgb, linToSrgb,
} from './oklch';
import type { Vision } from './cvd';
import {
    VISION_LABELS, visionContrast, simulatedDistance, simulateOklch,
} from './cvd';

export interface PaletteEditorProps {
    palette: Palette;
    locks: LockMap;
    ctx: SolverContext;
    onChange: (next: Palette) => void;
    onLocksChange: (next: LockMap) => void;
}

// Lightness slice + plotted chroma extent for the chromaticity disc.
// L=0.62 sits in the meat of typical role lightnesses (text/bg are far on
// the ends, but everything else lands near here), and cMax=0.22 keeps the
// disc almost entirely inside the sRGB gamut. Out-of-gamut pixels are
// clipped, not greyed — so the whole disc reads as colorful and opaque.
const CHROMA_BG_L = 0.62;
const CHROMA_MAX = 0.22;
const DRAG_THRESHOLD = 3;    // pixels before a press promotes to a drag

// Bitmap resolution for the canvas. Painting is one-shot at mount; the
// browser scales the CSS dimensions, so a single high-res bitmap can fill
// any responsive container without per-resize repaints.
const CHROMA_BITMAP = 720;
const PICKER_MIN = 320;
const PICKER_MAX = 800;
const LIGHT_W = 36;
const LIGHT_GAP = 16;
const CARD_BODY_PADDING = 32;

// Floating-label sizing. Width is estimated from text length × an em-width
// factor (no DOM measurement) so layout is purely deterministic.
const LABEL_FONT = 11;
const LABEL_PAD_X = 6;
const LABEL_PAD_Y = 3;
const LABEL_EM_W = 0.58;

const labelDims = (text: string) => ({
    w: text.length * LABEL_FONT * LABEL_EM_W + LABEL_PAD_X * 2,
    h: LABEL_FONT + LABEL_PAD_Y * 2,
});

// Place labels around a circular cluster of dots. Each label starts at a
// radial-outward offset from its dot (or, for centered dots, due-east) and
// is then nudged in axis-aligned-bbox collision passes until no two labels
// overlap. Pure-functional and deterministic — same input always gives the
// same layout, which keeps labels stable while the palette mutates.
function placeChromaLabels(
    items: Array<{ role: Role; x: number; y: number; w: number; h: number }>,
    cx: number,
    cy: number,
    dotR: number,
): Record<string, { x: number; y: number; w: number; h: number }> {
    const out: Record<string, { x: number; y: number; w: number; h: number }> = {};
    for (const it of items) {
        const dx = it.x - cx;
        const dy = it.y - cy;
        const dist = Math.hypot(dx, dy);
        const ux = dist > 0.5 ? dx / dist : 1;
        const uy = dist > 0.5 ? dy / dist : 0;
        const reach = dotR + 6 + it.h / 2;
        out[it.role] = { x: it.x + ux * reach, y: it.y + uy * reach, w: it.w, h: it.h };
    }
    for (let iter = 0; iter < 120; iter++) {
        let moved = false;
        for (let i = 0; i < items.length; i++) {
            for (let j = i + 1; j < items.length; j++) {
                const a = out[items[i].role];
                const b = out[items[j].role];
                const dx = b.x - a.x;
                const dy = b.y - a.y;
                const overlapX = (a.w + b.w) / 2 + 2 - Math.abs(dx);
                const overlapY = (a.h + b.h) / 2 + 1 - Math.abs(dy);
                if (overlapX > 0 && overlapY > 0) {
                    if (overlapX < overlapY) {
                        const push = overlapX / 2 + 0.25;
                        const sx = dx === 0 ? (i < j ? -1 : 1) : (dx > 0 ? 1 : -1);
                        a.x -= push * sx;
                        b.x += push * sx;
                    } else {
                        const push = overlapY / 2 + 0.25;
                        const sy = dy === 0 ? (i < j ? -1 : 1) : (dy > 0 ? 1 : -1);
                        a.y -= push * sy;
                        b.y += push * sy;
                    }
                    moved = true;
                }
            }
        }
        if (!moved) break;
    }
    return out;
}

// Stack labels along a vertical lightness column. Labels keep their
// preferred y (= dot.y) when free, otherwise are compressed top-down then
// rebalanced bottom-up so the run never overflows the column.
function placeLightLabels(
    items: Array<{ role: Role; y: number; w: number; h: number }>,
    x: number,
): Record<string, { x: number; y: number; w: number; h: number }> {
    const sorted = [...items].sort((a, b) => a.y - b.y);
    const ys: number[] = [];
    let prevBottom = -Infinity;
    for (const it of sorted) {
        const top = Math.max(it.y - it.h / 2, prevBottom + 1);
        ys.push(top);
        prevBottom = top + it.h;
    }
    const out: Record<string, { x: number; y: number; w: number; h: number }> = {};
    for (let i = 0; i < sorted.length; i++) {
        const it = sorted[i];
        out[it.role] = { x, y: ys[i] + it.h / 2, w: it.w, h: it.h };
    }
    return out;
}

export const PaletteEditor: React.FC<PaletteEditorProps> = (props) => (
    <div
        className="palette-editor"
        style={{ display: 'grid', gridTemplateColumns: 'minmax(280px, 1fr) 2fr', gap: '1rem' }}
    >
        <PreviewColumn palette={props.palette} vision={props.ctx.vision ?? 'normal'} />
        <PickerColumn {...props} />
    </div>
);

// ---- preview column ----------------------------------------------------

// Both cards render through the active vision model: the preview shows the
// palette as that viewer sees it, and the report scores it against the
// criteria the solver is actually optimizing. A picker that previewed one
// thing and solved for another would be worse than no CVD mode at all.
const PreviewColumn: React.FC<{ palette: Palette; vision: Vision }> = ({ palette, vision }) => (
    <div className="d-flex flex-column gap-3">
        <Card>
            <Card.Header className="d-flex justify-content-between align-items-center">
                <span>Preview</span>
                {vision !== 'normal' && (
                    <span className="text-muted small fw-normal">
                        as seen with {VISION_LABELS[vision].toLowerCase()}
                    </span>
                )}
            </Card.Header>
            <Card.Body className="p-0">
                <DemoPreview palette={palette} vision={vision} />
            </Card.Body>
        </Card>
        <Card>
            <Card.Header>Contrast</Card.Header>
            <Card.Body>
                <ContrastReport palette={palette} vision={vision} />
            </Card.Body>
        </Card>
    </div>
);

const DemoPreview: React.FC<{ palette: Palette; vision: Vision }> = ({ palette, vision }) => {
    const c = (r: Role) => rgbToHex(simulateOklch(palette[r], vision));
    return (
        <div
            style={{
                background: c('bg'),
                color: c('text'),
                padding: '1.25rem',
                transition: 'background 80ms linear',
            }}
        >
            <div className="d-flex justify-content-between align-items-center mb-3">
                <div style={{ fontSize: 22, fontWeight: 700 }}>Demo Page</div>
                <div className="d-flex gap-2">
                    <button style={demoBtn(c('primary'), c('onPrimary'), c('primary'))}>Primary</button>
                    <button style={demoBtn('transparent', c('text'), c('border'))}>Outline</button>
                    <button style={demoBtn('transparent', c('link'), 'transparent')}>Link →</button>
                </div>
            </div>
            <p style={{ fontSize: 13, lineHeight: 1.55, marginBottom: '1rem' }}>
                Lorem ipsum dolor sit amet, consectetur adipiscing elit. Sed do
                eiusmod tempor incididunt ut labore et dolore magna aliqua. Ut
                enim ad minim veniam, quis nostrud exercitation ullamco laboris.
            </p>
            <div
                style={{
                    background: c('surface'),
                    border: `1px solid ${c('border')}`,
                    borderRadius: 'var(--bs-border-radius)',
                    padding: '1rem',
                    marginBottom: '1rem',
                }}
            >
                <div style={{ fontSize: 16, fontWeight: 700, marginBottom: '0.5rem' }}>Card surface</div>
                <div style={{ color: c('textMuted'), fontSize: 13, marginBottom: '0.5rem' }}>
                    Muted body copy. The quick brown fox jumps over the lazy dog.
                </div>
                <div style={{ fontSize: 13, marginBottom: '0.5rem' }}>
                    inline <code style={{ color: c('code'), fontFamily: 'inherit' }}>code()</code> sample
                </div>
                <a style={{ color: c('link'), textDecoration: 'underline' }}>An inline link</a>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.5rem' }}>
                {(['success', 'warning', 'danger', 'info'] as Role[]).map((r) => (
                    <div
                        key={r}
                        style={{
                            background: `color-mix(in srgb, ${c(r)} 14%, ${c('bg')})`,
                            border: `1px solid ${c(r)}`,
                            color: c('text'),
                            padding: '0.5rem 0.75rem',
                            borderRadius: 'var(--bs-border-radius)',
                            fontSize: 13,
                        }}
                    >
                        {/* Lead-in keeps the role hue but borrows the text
                            color's contrast so it stays readable on darker
                            preset bgs where role-vs-bg ratio bottoms out. */}
                        <strong style={{
                            textTransform: 'capitalize',
                            color: `color-mix(in srgb, ${c(r)} 55%, ${c('text')})`,
                        }}>{r}:</strong>{' '}
                        example callout text.
                    </div>
                ))}
            </div>
        </div>
    );
};

const demoBtn = (bg: string, fg: string, border: string): React.CSSProperties => ({
    background: bg,
    color: fg,
    border: `1px solid ${border}`,
    padding: '6px 12px',
    borderRadius: 'var(--bs-border-radius)',
    fontFamily: 'inherit',
    fontSize: 12,
    fontWeight: 700,
    cursor: 'pointer',
    transition: 'background 80ms linear, color 80ms linear, border-color 80ms linear',
});

// ---- picker column -----------------------------------------------------

type DragState =
    | { kind: 'down'; role: Role; axis: 'ab' | 'L'; startX: number; startY: number }
    | { kind: 'drag'; role: Role; axis: 'ab' | 'L'; clientX: number; clientY: number };

const PickerColumn: React.FC<PaletteEditorProps> = ({
    palette, locks, ctx, onChange, onLocksChange,
}) => {
    const [selected, setSelected] = useState<Role | null>(null);
    const [solverRunning, setSolverRunning] = useState(false);
    // Drag position lives in a ref so mouse-move doesn't force re-render on
    // every pixel; the rAF solver loop polls the ref directly. Only drag
    // *state changes* (down/drag/up) trigger setState.
    const dragRef = useRef<DragState | null>(null);
    const [dragVersion, setDragVersion] = useState(0);
    const bumpDrag = () => setDragVersion((v) => v + 1);

    const cardBodyRef = useRef<HTMLDivElement>(null);
    const chromaSvgRef = useRef<SVGSVGElement>(null);
    const lightSvgRef = useRef<SVGSVGElement>(null);
    const [pickerSize, setPickerSize] = useState(480);

    // Resize the chromaticity disc to whatever space the card body has.
    // We measure the card body width and reserve room for the lightness
    // column + gap + body padding, then square the result so the disc
    // stays circular.
    useEffect(() => {
        const el = cardBodyRef.current;
        if (!el || typeof ResizeObserver === 'undefined') return;
        const measure = () => {
            const w = el.clientWidth;
            const avail = w - LIGHT_W - LIGHT_GAP - CARD_BODY_PADDING;
            const next = Math.max(PICKER_MIN, Math.min(PICKER_MAX, avail));
            setPickerSize(next);
        };
        measure();
        const ro = new ResizeObserver(measure);
        ro.observe(el);
        return () => ro.disconnect();
    }, []);

    // Latest props for the solver loop's stable closure.
    const latest = useRef({ palette, locks, ctx, onChange });
    latest.current = { palette, locks, ctx, onChange };

    // ---- solver tick ----
    useEffect(() => {
        if (!solverRunning) return;
        let raf = 0;
        const tick = () => {
            const { palette, locks, ctx, onChange } = latest.current;
            let p = palette;
            const drag = dragRef.current;
            // Apply pending drag update (the user's pointer position) BEFORE
            // the gradient step so the next render reflects both in one pass.
            if (drag?.kind === 'drag') {
                const next = positionToColor(drag, p);
                if (next) p = { ...p, [drag.role]: next };
            }
            const r = gradientStep(p, locks, ctx);
            onChange(r.palette);
            const stillDragging = drag?.kind === 'drag';
            if ((r.stalled || r.gradNorm < 1e-3) && !stillDragging) {
                setSolverRunning(false);
            } else {
                raf = requestAnimationFrame(tick);
            }
        };
        raf = requestAnimationFrame(tick);
        return () => cancelAnimationFrame(raf);
    }, [solverRunning]);

    // ---- mouse handlers (window-level so drag survives leaving a dot) ----
    useEffect(() => {
        const onMove = (e: MouseEvent) => {
            const d = dragRef.current;
            if (!d) return;
            if (d.kind === 'down') {
                const dx = e.clientX - d.startX;
                const dy = e.clientY - d.startY;
                if (Math.hypot(dx, dy) > DRAG_THRESHOLD) {
                    // Promote to drag: lock the role and start the solver.
                    const cur = latest.current;
                    if (!cur.locks[d.role]) {
                        onLocksChange({ ...cur.locks, [d.role]: true });
                    }
                    dragRef.current = {
                        kind: 'drag', role: d.role, axis: d.axis,
                        clientX: e.clientX, clientY: e.clientY,
                    };
                    setSolverRunning(true);
                    bumpDrag();
                }
            } else {
                dragRef.current = { ...d, clientX: e.clientX, clientY: e.clientY };
            }
        };
        const onUp = () => {
            const d = dragRef.current;
            if (d?.kind === 'down') {
                // No movement past threshold ⇒ treat as a select-click.
                setSelected(d.role);
            }
            dragRef.current = null;
            bumpDrag();
        };
        window.addEventListener('mousemove', onMove);
        window.addEventListener('mouseup', onUp);
        return () => {
            window.removeEventListener('mousemove', onMove);
            window.removeEventListener('mouseup', onUp);
        };
    }, [onLocksChange]);

    const onDotMouseDown = (
        e: React.MouseEvent,
        role: Role,
        axis: 'ab' | 'L',
    ) => {
        if (e.button !== 0) return;
        e.preventDefault();
        e.stopPropagation();
        dragRef.current = {
            kind: 'down', role, axis,
            startX: e.clientX, startY: e.clientY,
        };
        bumpDrag();
    };

    const onDotContextMenu = (e: React.MouseEvent, role: Role) => {
        e.preventDefault();
        e.stopPropagation();
        if (locks[role]) onLocksChange({ ...locks, [role]: false });
    };

    const onBackgroundClick = (e: React.MouseEvent) => {
        // Click in empty picker space deselects.
        if (e.target === e.currentTarget) setSelected(null);
    };

    const positionToColor = (
        drag: Extract<DragState, { kind: 'drag' }>,
        p: Palette,
    ): OKLCH | null => {
        const cur = p[drag.role];
        if (drag.axis === 'ab') {
            const svg = chromaSvgRef.current;
            if (!svg) return null;
            const rect = svg.getBoundingClientRect();
            const cx = rect.width / 2, cy = rect.height / 2;
            const maxR = Math.min(cx, cy) - 14;
            const scale = maxR / CHROMA_MAX;
            const ax = (drag.clientX - rect.left - cx) / scale;
            const ay = -(drag.clientY - rect.top - cy) / scale;
            const C = Math.min(CHROMA_MAX, Math.hypot(ax, ay));
            const h = (Math.atan2(ay, ax) * 180 / Math.PI + 360) % 360;
            return { L: cur.L, C, h };
        }
        const svg = lightSvgRef.current;
        if (!svg) return null;
        const rect = svg.getBoundingClientRect();
        const L = clamp(1 - (drag.clientY - rect.top) / rect.height, 0.02, 0.98);
        return { L, C: cur.C, h: cur.h };
    };

    const isLocked = (r: Role) => !!locks[r];
    const drag = dragRef.current; // re-read after every render via dragVersion
    void dragVersion;
    const draggingRole = drag?.kind === 'drag' ? drag.role : null;

    return (
        <Card style={{ height: '100%' }}>
            <Card.Header className="d-flex justify-content-between align-items-center">
                <span>Color Picker</span>
                <span className="text-muted small fw-normal">
                    drag to lock · right-click to unlock
                </span>
            </Card.Header>
            <Card.Body
                ref={cardBodyRef}
                className="d-flex justify-content-center align-items-center"
                style={{ gap: LIGHT_GAP, padding: CARD_BODY_PADDING / 2 }}
                onMouseDown={onBackgroundClick}
            >
                <ChromaticityPanel
                    size={pickerSize}
                    svgRef={chromaSvgRef}
                    palette={palette}
                    isLocked={isLocked}
                    selected={selected}
                    draggingRole={draggingRole}
                    onDotMouseDown={(e, r) => onDotMouseDown(e, r, 'ab')}
                    onDotContextMenu={onDotContextMenu}
                />
                <LightnessPanel
                    height={pickerSize}
                    svgRef={lightSvgRef}
                    palette={palette}
                    isLocked={isLocked}
                    selected={selected}
                    draggingRole={draggingRole}
                    onDotMouseDown={(e, r) => onDotMouseDown(e, r, 'L')}
                    onDotContextMenu={onDotContextMenu}
                />
            </Card.Body>
            <Card.Footer>
                {selected ? (
                    <SelectedInfo
                        role={selected}
                        palette={palette}
                        locked={!!locks[selected]}
                        onLockToggle={() =>
                            onLocksChange({ ...locks, [selected]: !locks[selected] })
                        }
                        onChange={onChange}
                        onLocksChange={onLocksChange}
                        locks={locks}
                        onClose={() => setSelected(null)}
                    />
                ) : (
                    <span className="text-muted small fst-italic">
                        Click a color in the picker to inspect it.
                    </span>
                )}
            </Card.Footer>
        </Card>
    );
};

// ---- chromaticity panel ------------------------------------------------

interface PanelProps {
    svgRef: React.RefObject<SVGSVGElement>;
    palette: Palette;
    isLocked: (r: Role) => boolean;
    selected: Role | null;
    draggingRole: Role | null;
    onDotMouseDown: (e: React.MouseEvent, r: Role) => void;
    onDotContextMenu: (e: React.MouseEvent, r: Role) => void;
}

const ChromaticityPanel: React.FC<PanelProps & { size: number }> = ({
    size, svgRef, palette, isLocked, selected, draggingRole, onDotMouseDown, onDotContextMenu,
}) => {
    // Paint once at the fixed bitmap resolution; CSS scales to the live
    // container size. No repaint on resize, so we never get a flash of
    // unpainted canvas while the user adjusts their window.
    const canvasRef = useRef<HTMLCanvasElement>(null);
    useEffect(() => {
        const canvas = canvasRef.current;
        if (!canvas) return;
        paintChromaticityCanvas(canvas, CHROMA_BG_L, CHROMA_MAX);
    }, []);

    const cx = size / 2, cy = size / 2;
    const maxR = (size / 2) - 14;
    const scale = maxR / CHROMA_MAX;

    const dots = useMemo(() => ROLES.map((r) => {
        const c = palette[r];
        const rad = (c.h * Math.PI) / 180;
        const dim = labelDims(ROLE_LABELS[r]);
        return {
            role: r,
            x: cx + Math.cos(rad) * c.C * scale,
            y: cy - Math.sin(rad) * c.C * scale,
            color: oklchToHex(c),
            lw: dim.w,
            lh: dim.h,
        };
    }), [palette, cx, cy, scale]);

    // Anchor each label to its dot by a fixed pixel offset, computed once
    // from the very first dot layout. As the palette changes, labels glide
    // with their dots instead of jittering through a fresh layout pass.
    const labelOffsetsRef = useRef<Record<string, { dx: number; dy: number }> | null>(null);
    if (!labelOffsetsRef.current && dots.length > 0) {
        const placed = placeChromaLabels(
            dots.map((d) => ({ role: d.role, x: d.x, y: d.y, w: d.lw, h: d.lh })),
            cx, cy, 9,
        );
        const offs: Record<string, { dx: number; dy: number }> = {};
        for (const d of dots) {
            offs[d.role] = { dx: placed[d.role].x - d.x, dy: placed[d.role].y - d.y };
        }
        labelOffsetsRef.current = offs;
    }
    const labelOffsets = labelOffsetsRef.current ?? {};

    return (
        <div style={{ position: 'relative', width: size, height: size, flex: '0 0 auto' }}>
            <canvas
                ref={canvasRef}
                width={CHROMA_BITMAP}
                height={CHROMA_BITMAP}
                style={{
                    position: 'absolute', inset: 0,
                    width: '100%', height: '100%',
                    borderRadius: '50%',
                    pointerEvents: 'none',
                }}
            />
            <svg
                ref={svgRef}
                width={size}
                height={size}
                viewBox={`0 0 ${size} ${size}`}
                style={{ position: 'absolute', inset: 0, overflow: 'visible' }}
            >
                <defs>
                    <clipPath id="chroma-clip">
                        <circle cx={cx} cy={cy} r={maxR + 8} />
                    </clipPath>
                </defs>
                <circle cx={cx} cy={cy} r={maxR + 8} fill="none"
                    stroke="rgba(255,255,255,0.35)" strokeWidth={1} />
                <g clipPath="url(#chroma-clip)" pointerEvents="none">
                    {[0.06, 0.12, 0.18].map((c, i) => (
                        <circle
                            key={i}
                            cx={cx}
                            cy={cy}
                            r={c * scale}
                            fill="none"
                            stroke="rgba(0,0,0,0.18)"
                            strokeWidth={1}
                        />
                    ))}
                    <line x1={cx} y1={cy - maxR} x2={cx} y2={cy + maxR}
                        stroke="rgba(0,0,0,0.15)" strokeWidth={1} />
                    <line x1={cx - maxR} y1={cy} x2={cx + maxR} y2={cy}
                        stroke="rgba(0,0,0,0.15)" strokeWidth={1} />
                </g>
                {/* Connector lines + labels render under the dots so dots
                    always remain directly grabbable. */}
                {dots.map((d) => {
                    const off = labelOffsets[d.role];
                    if (!off) return null;
                    const lx = d.x + off.dx;
                    const ly = d.y + off.dy;
                    return (
                        <line
                            key={`line-${d.role}`}
                            x1={d.x} y1={d.y} x2={lx} y2={ly}
                            stroke={d.color}
                            strokeOpacity={0.45}
                            strokeWidth={1}
                            pointerEvents="none"
                        />
                    );
                })}
                {dots.map((d) => {
                    const off = labelOffsets[d.role];
                    if (!off) return null;
                    const lx = d.x + off.dx;
                    const ly = d.y + off.dy;
                    return (
                        <LabelChip
                            key={`lbl-${d.role}`}
                            x={lx} y={ly} w={d.lw} h={d.lh}
                            color={d.color}
                            text={ROLE_LABELS[d.role]}
                            onMouseDown={(e) => onDotMouseDown(e, d.role)}
                            onContextMenu={(e) => onDotContextMenu(e, d.role)}
                        />
                    );
                })}
                {dots.map((d) => {
                    const locked = isLocked(d.role);
                    const isSelected = selected === d.role;
                    const isDragging = draggingRole === d.role;
                    const r = isSelected || isDragging ? 12 : 9;
                    return (
                        <g key={d.role}>
                            <circle
                                cx={d.x}
                                cy={d.y}
                                r={r}
                                fill={d.color}
                                stroke={isSelected ? 'white' : 'rgba(0,0,0,0.7)'}
                                strokeWidth={isSelected ? 2.5 : 1.5}
                                style={{ cursor: 'grab' }}
                                onMouseDown={(e) => onDotMouseDown(e, d.role)}
                                onContextMenu={(e) => onDotContextMenu(e, d.role)}
                            />
                            {locked && (
                                <LockGlyph cx={d.x} cy={d.y} size={r * 1.2} />
                            )}
                        </g>
                    );
                })}
            </svg>
        </div>
    );
};

// ---- lightness panel ---------------------------------------------------

const LightnessPanel: React.FC<PanelProps & { height: number }> = ({
    height, svgRef, palette, isLocked, selected, draggingRole, onDotMouseDown, onDotContextMenu,
}) => {
    const dots = useMemo(() => ROLES.map((r) => {
        const c = palette[r];
        const dim = labelDims(ROLE_LABELS[r]);
        return {
            role: r,
            y: (1 - c.L) * height,
            color: oklchToHex(c),
            lw: dim.w,
            lh: dim.h,
        };
    }), [palette, height]);

    // Compute each label's vertical offset from its dot once, on first
    // layout, so the labels travel with their dots during edits instead
    // of jumping into a new compaction order.
    const labelOffsetsRef = useRef<Record<string, number> | null>(null);
    if (!labelOffsetsRef.current && dots.length > 0) {
        const placed = placeLightLabels(
            dots.map((d) => ({ role: d.role, y: d.y, w: d.lw, h: d.lh })),
            LIGHT_W + 12,
        );
        const offs: Record<string, number> = {};
        for (const d of dots) offs[d.role] = placed[d.role].y - d.y;
        labelOffsetsRef.current = offs;
    }
    const labelOffsets = labelOffsetsRef.current ?? {};

    return (
        <svg
            ref={svgRef}
            width={LIGHT_W}
            height={height}
            style={{ flex: '0 0 auto', overflow: 'visible' }}
        >
            <defs>
                <linearGradient id="lgrad" x1="0" x2="0" y1="0" y2="1">
                    <stop offset="0%" stopColor="white" />
                    <stop offset="100%" stopColor="black" />
                </linearGradient>
            </defs>
            <rect x={0} y={0} width={LIGHT_W} height={height} fill="url(#lgrad)" rx={6} />
            {dots.map((d) => {
                const dy = labelOffsets[d.role] ?? 0;
                const ly = d.y + dy;
                const lx = LIGHT_W + 12 + d.lw / 2;
                return (
                    <line
                        key={`line-${d.role}`}
                        x1={LIGHT_W / 2} y1={d.y} x2={lx - d.lw / 2} y2={ly}
                        stroke={d.color}
                        strokeOpacity={0.45}
                        strokeWidth={1}
                        pointerEvents="none"
                    />
                );
            })}
            {dots.map((d) => {
                const dy = labelOffsets[d.role] ?? 0;
                const ly = d.y + dy;
                const lx = LIGHT_W + 12 + d.lw / 2;
                return (
                    <LabelChip
                        key={`lbl-${d.role}`}
                        x={lx} y={ly} w={d.lw} h={d.lh}
                        color={d.color}
                        text={ROLE_LABELS[d.role]}
                        onMouseDown={(e) => onDotMouseDown(e, d.role)}
                        onContextMenu={(e) => onDotContextMenu(e, d.role)}
                    />
                );
            })}
            {dots.map((d) => {
                const locked = isLocked(d.role);
                const isSelected = selected === d.role;
                const isDragging = draggingRole === d.role;
                const r = isSelected || isDragging ? 7 : 5;
                return (
                    <g key={d.role}>
                        <circle
                            cx={LIGHT_W / 2}
                            cy={d.y}
                            r={r}
                            fill={d.color}
                            stroke={isSelected ? 'rgba(255,255,255,0.95)' : 'rgba(0,0,0,0.6)'}
                            strokeWidth={isSelected ? 2 : 1.5}
                            style={{ cursor: 'grab' }}
                            onMouseDown={(e) => onDotMouseDown(e, d.role)}
                            onContextMenu={(e) => onDotContextMenu(e, d.role)}
                        />
                        {locked && <LockGlyph cx={LIGHT_W / 2} cy={d.y} size={r * 1.2} />}
                    </g>
                );
            })}
        </svg>
    );
};

// ---- floating label chip ----------------------------------------------

const LabelChip: React.FC<{
    x: number; y: number; w: number; h: number;
    color: string; text: string;
    onMouseDown: (e: React.MouseEvent) => void;
    onContextMenu: (e: React.MouseEvent) => void;
}> = ({ x, y, w, h, color, text, onMouseDown, onContextMenu }) => (
    <g
        transform={`translate(${x - w / 2} ${y - h / 2})`}
        style={{ cursor: 'grab' }}
        onMouseDown={onMouseDown}
        onContextMenu={onContextMenu}
    >
        <rect
            x={0} y={0} width={w} height={h} rx={3}
            fill="var(--bs-body-bg, #fff)"
            fillOpacity={0.92}
            stroke={color}
            strokeWidth={1}
        />
        <text
            x={w / 2} y={h / 2 + 0.5}
            textAnchor="middle"
            dominantBaseline="middle"
            fontSize={LABEL_FONT}
            fontFamily="var(--bs-font-sans-serif, sans-serif)"
            fill="var(--bs-body-color, #000)"
            style={{ userSelect: 'none', pointerEvents: 'none' }}
        >
            {text}
        </text>
    </g>
);

// ---- lock glyph (small overlay shown on locked dots) -------------------

const LockGlyph: React.FC<{ cx: number; cy: number; size: number }> = ({ cx, cy, size }) => {
    // A tiny padlock outline drawn in white to read against any dot fill.
    const w = size, h = size * 1.1;
    const x = cx - w / 2, y = cy - h / 2;
    return (
        <g pointerEvents="none">
            <rect
                x={x + w * 0.2}
                y={y + h * 0.45}
                width={w * 0.6}
                height={h * 0.45}
                rx={1.2}
                fill="white"
                stroke="rgba(0,0,0,0.7)"
                strokeWidth={0.5}
            />
            <path
                d={`M ${x + w * 0.32} ${y + h * 0.45}
                    V ${y + h * 0.28}
                    a ${w * 0.18} ${h * 0.18} 0 0 1 ${w * 0.36} 0
                    V ${y + h * 0.45}`}
                fill="none"
                stroke="white"
                strokeWidth={1.5}
            />
        </g>
    );
};

// ---- selected-color info bar ------------------------------------------

const SelectedInfo: React.FC<{
    role: Role;
    palette: Palette;
    locked: boolean;
    locks: LockMap;
    onLockToggle: () => void;
    onChange: (next: Palette) => void;
    onLocksChange: (next: LockMap) => void;
    onClose: () => void;
}> = ({ role, palette, locked, locks, onLockToggle, onChange, onLocksChange, onClose }) => {
    const hex = oklchToHex(palette[role]);
    const [draft, setDraft] = useState(hex);

    // Snap the input back to the canonical hex whenever the role changes
    // or the palette is mutated externally (drag, solver step, etc.) so
    // the field always reflects truth between active edits.
    useEffect(() => {
        setDraft(hex);
    }, [hex, role]);

    const tryCommit = (text: string) => {
        const parsed = hexToOklch(text);
        if (!parsed) return false;
        onChange({ ...palette, [role]: parsed });
        if (!locks[role]) onLocksChange({ ...locks, [role]: true });
        return true;
    };

    return (
        <div className="d-flex align-items-center gap-3 flex-wrap">
            <div
                style={{
                    width: 32, height: 32, borderRadius: 4, flex: '0 0 auto',
                    background: hex,
                    border: '1px solid var(--bs-border-color)',
                }}
            />
            <div className="d-flex flex-column" style={{ minWidth: 0 }}>
                <strong>{ROLE_LABELS[role]}</strong>
                <InlineText
                    value={draft}
                    onChange={(e) => {
                        const v = e.target.value;
                        setDraft(v);
                        tryCommit(v);
                    }}
                    onBlur={() => setDraft(hex)}
                    spellCheck={false}
                    autoCapitalize="off"
                    autoCorrect="off"
                    minWidth={7}
                    style={{ fontSize: '0.875em' }}
                    aria-label={`${ROLE_LABELS[role]} hex`}
                />
            </div>
            <div className="ms-auto d-flex gap-2">
                <Button
                    variant={locked ? 'primary' : 'outline-secondary'}
                    size="sm"
                    onClick={onLockToggle}
                >
                    {locked ? <BsLockFill className="me-2" /> : <BsUnlockFill className="me-2" />}
                    {locked ? 'Locked' : 'Unlocked'}
                </Button>
                <Button variant="link" size="sm" onClick={onClose}>
                    Close
                </Button>
            </div>
        </div>
    );
};

// ---- contrast report (still shown — only loss is hidden) --------------

const REPORT_PAIRS: Array<{ a: Role; b: Role; aim: number; label: string }> = [
    { a: 'text', b: 'bg', aim: 7.0, label: 'text / bg' },
    { a: 'textMuted', b: 'bg', aim: 4.5, label: 'muted / bg' },
    { a: 'border', b: 'bg', aim: 3.0, label: 'border / bg' },
    { a: 'primary', b: 'bg', aim: 3.0, label: 'primary / bg' },
    { a: 'onPrimary', b: 'primary', aim: 4.5, label: 'onPrimary / primary' },
    { a: 'secondary', b: 'bg', aim: 3.0, label: 'secondary / bg' },
    { a: 'onSecondary', b: 'secondary', aim: 4.5, label: 'onSecondary / secondary' },
    { a: 'link', b: 'bg', aim: 4.5, label: 'link / bg' },
    { a: 'onSuccess', b: 'success', aim: 4.5, label: 'onSuccess / success' },
    { a: 'onWarning', b: 'warning', aim: 4.5, label: 'onWarning / warning' },
    { a: 'onDanger', b: 'danger', aim: 4.5, label: 'onDanger / danger' },
    { a: 'onInfo', b: 'info', aim: 4.5, label: 'onInfo / info' },
    { a: 'code', b: 'surface', aim: 3.5, label: 'code / surface' },
];

// Pass/fail can't be carried by colour alone in a panel whose whole subject
// is colour vision — under achromatopsia the green and the amber are the same
// pixel. Each row gets a leading glyph so the verdict survives the palette
// it's reporting on.
const Verdict: React.FC<{ ok: boolean; label: string; value: string }> = ({ ok, label, value }) => (
    <div
        className="d-flex justify-content-between small"
        style={{ color: `var(--bs-${ok ? 'success' : 'warning'})` }}
    >
        <span>
            <span aria-hidden="true" className="me-1">{ok ? '✓' : '!'}</span>
            {label}
        </span>
        <span>{value}</span>
    </div>
);

// The WCAG floors don't move under a CVD model — the ratios do, because they
// are measured on the simulated colors. The second group is the criterion
// that only a CVD model brings: two colors meaning different things staying
// far enough apart to be told apart.
const ContrastReport: React.FC<{ palette: Palette; vision: Vision }> = ({ palette, vision }) => (
    <div className="d-flex flex-column" style={{ gap: 4 }}>
        {REPORT_PAIRS.map((p) => {
            const r = visionContrast(palette[p.a], palette[p.b], vision);
            return (
                <Verdict
                    key={p.label}
                    ok={r >= p.aim}
                    label={p.label}
                    value={`${r.toFixed(2)} / ${p.aim.toFixed(1)}`}
                />
            );
        })}
        <div className="text-muted small mt-2">Separation (ΔE)</div>
        {SEPARATE_PAIRS.map((s) => {
            const floor = separationFloor(s, vision);
            const d = simulatedDistance(palette[s.a], palette[s.b], vision);
            return (
                <Verdict
                    key={`${s.a}/${s.b}`}
                    ok={d >= floor}
                    label={`${s.a} ↔ ${s.b}`}
                    value={`${d.toFixed(3)} / ${floor.toFixed(2)}`}
                />
            );
        })}
    </div>
);

// ---- canvas paint ------------------------------------------------------

// Render the OKLCH chromaticity gradient at a fixed lightness slice. For
// each pixel we map (x, y) → (a*, b*), run OKLab → linear sRGB, clip into
// [0, 1] (so the rim stays vivid instead of muted-grey), then encode to
// 8-bit display sRGB at full opacity. The disc is masked to a circle by
// emitting alpha=0 outside the radius — that's the *only* transparency in
// the bitmap, so the picker reads as a solid colorful surface.
function paintChromaticityCanvas(canvas: HTMLCanvasElement, L: number, cMax: number) {
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    const w = canvas.width, h = canvas.height;
    const cx = w / 2, cy = h / 2;
    const maxR = Math.min(cx, cy);
    const scale = maxR / cMax;
    const img = ctx.createImageData(w, h);
    const data = img.data;
    const clamp01 = (v: number) => v < 0 ? 0 : v > 1 ? 1 : v;
    for (let y = 0; y < h; y++) {
        for (let x = 0; x < w; x++) {
            const idx = (y * w + x) * 4;
            const dx = x - cx, dy = y - cy;
            if (dx * dx + dy * dy > maxR * maxR) {
                data[idx + 3] = 0;
                continue;
            }
            const a = (x - cx) / scale;
            const b = -(y - cy) / scale;
            const lin = oklabToLinearRgb(L, a, b);
            data[idx]     = Math.round(linToSrgb(clamp01(lin.r)) * 255);
            data[idx + 1] = Math.round(linToSrgb(clamp01(lin.g)) * 255);
            data[idx + 2] = Math.round(linToSrgb(clamp01(lin.b)) * 255);
            data[idx + 3] = 255;
        }
    }
    ctx.putImageData(img, 0, 0);
}

// ---- helpers -----------------------------------------------------------

const clamp = (v: number, lo: number, hi: number) =>
    v < lo ? lo : v > hi ? hi : v;

export default PaletteEditor;
